import { useEffect, useMemo, useState } from "react";
import { getPedidoById, getPedidoHistorial } from "../../../services/admin/pedidos_admin";
import "./OrderTimelineModal.css";
import {
  IconOrderPending,
  IconOrderProcessing,
  IconOrderShipped,
  IconOrderDelivered,
  IconOrderCanceled,
} from "../../../icons/Icons";

const ESTADOS = [
  { id: 0, nombre: "Pending" },
  { id: 1, nombre: "Processing" },
  { id: 2, nombre: "Shipped" },
  { id: 3, nombre: "Delivered" },
  { id: 4, nombre: "Canceled" },
];

const ORDER_STATUS_ICONS = {
  0: IconOrderPending,
  1: IconOrderProcessing,
  2: IconOrderShipped,
  3: IconOrderDelivered,
  4: IconOrderCanceled,
};

const formatDate = (dt) => {
  if (!dt) return "—";
  const d = new Date(dt);
  if (Number.isNaN(d.getTime())) return String(dt).split("T")[0] || "—";
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  const hh = String(d.getHours()).padStart(2, "0");
  const mi = String(d.getMinutes()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd} ${hh}:${mi}`;
};

const fullName = (u) => (u ? `${u.nombre || ""} ${u.apellido || ""}`.trim() || "—" : "—");

const toTime = (v) => {
  const t = new Date(v || 0).getTime();
  return Number.isFinite(t) ? t : 0;
};

const money = (v) => {
  const n = Number(v);
  if (!Number.isFinite(n)) return "—";
  return `$ ${n.toFixed(2)}`;
};

function StatusIcon({ estadoId }) {
  const Icon = ORDER_STATUS_ICONS[Number(estadoId)];
  if (!Icon) return null;
  return <Icon />;
}

function OrderTimelineModal({ pedidoId, onClose }) {
  const [pedido, setPedido] = useState(null);
  const [historial, setHistorial] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!pedidoId) return;

    let mounted = true;

    const onKey = (e) => {
      if (e.key === "Escape") onClose?.();
    };

    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    window.addEventListener("keydown", onKey);

    const load = async () => {
      setLoading(true);
      try {
        const [p, h] = await Promise.all([getPedidoById(pedidoId), getPedidoHistorial(pedidoId)]);
        if (!mounted) return;
        setPedido(p || null);
        setHistorial(Array.isArray(h) ? h : []);
      } catch (e) {
        console.error("Error loading timeline:", e);
        if (mounted) {
          setPedido(null);
          setHistorial([]);
        }
      } finally {
        if (mounted) setLoading(false);
      }
    };

    load();

    return () => {
      mounted = false;
      document.body.style.overflow = prev;
      window.removeEventListener("keydown", onKey);
    };
  }, [pedidoId, onClose]);

  const estadoActual = Number(pedido?.estado ?? -1);
  const isCancel = estadoActual === 4;

  const estadoNombre =
    pedido?.estado_nombre || ESTADOS.find((e) => e.id === estadoActual)?.nombre || "—";

  const stepInfo = useMemo(() => {
    const byNuevo = new Map();

    for (const row of historial) {
      const key = Number(row.estado_nuevo);
      const prev = byNuevo.get(key);

      const tRow = toTime(row.fecha_creado || row.fecha);
      const tPrev = prev ? toTime(prev.fecha_creado || prev.fecha) : -1;

      if (!prev || tRow >= tPrev) byNuevo.set(key, row);
    }

    const fechasBase = {
      0: pedido?.fecha_creado,
      2: pedido?.fecha_enviado,
      3: pedido?.fecha_entregado,
      4: pedido?.fecha_cancelado,
    };

    return ESTADOS.map((s) => {
      const h = byNuevo.get(s.id);

      return {
        ...s,
        fecha: h?.fecha_creado || fechasBase[s.id] || null,
        usuario: h ? { nombre: h.nombre, apellido: h.apellido, usuario: h.usuario } : null,
        comentario: h?.comentario || null,
      };
    });
  }, [historial, pedido]);

  const progress = useMemo(() => {
    if (estadoActual < 0) return 0;
    if (isCancel) return 100;
    const max = 3;
    const clamped = Math.max(0, Math.min(max, estadoActual));
    return Math.round(((clamped + 1) / (max + 1)) * 100);
  }, [estadoActual, isCancel]);

  const lastChange = useMemo(() => {
    if (!historial.length) return null;
    const sorted = [...historial].sort(
      (a, b) => toTime(b.fecha_creado || b.fecha) - toTime(a.fecha_creado || a.fecha)
    );
    return sorted[0] || null;
  }, [historial]);

  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) onClose?.();
  };

  return (
    <div className="omx-tl" onMouseDown={handleBackdropClick}>
      <div className="omx-tl__overlay" />

      <div
        className="omx-tl__modal"
        onMouseDown={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
      >
        <div className="omx-tl__header">
          <div className="omx-tl__headLeft">
            <div className="omx-tl__kicker">Order Timeline</div>

            <div className="omx-tl__titleRow">
              <div className="omx-tl__title">
                Sales Order #{pedidoId}
                <span
                  className={`omx-tl__pill ${
                    isCancel ? "is-danger" : estadoActual === 3 ? "is-ok" : "is-info"
                  }`}
                >
                  {estadoNombre}
                </span>
              </div>

              <div className="omx-tl__progressWrap" aria-label="Progress">
                <div className="omx-tl__progressMeta">
                  <span className="omx-tl__progressLabel">Progress</span>
                  <span className="omx-tl__progressPct">{progress}%</span>
                </div>
                <div className={`omx-tl__progressBar ${isCancel ? "is-danger" : ""}`}>
                  <div className="omx-tl__progressFill" style={{ width: `${progress}%` }} />
                </div>
              </div>
            </div>
          </div>

          <button className="omx-tl__close" onClick={onClose} aria-label="Close">
            ×
          </button>
        </div>

        {loading ? (
          <div className="omx-tl__loading">
            <div className="omx-tl__skeletonRow" />
            <div className="omx-tl__skeletonRow" />
            <div className="omx-tl__skeletonRow" />
            <div className="omx-tl__skeletonRow" />
          </div>
        ) : (
          <div className="omx-tl__content">
            <div className="omx-tl__left">
              <div className="omx-tl__stepper">
                {stepInfo.map((s, idx) => {
                  const isActive = s.id === estadoActual;
                  const isDone = isCancel ? s.id === 0 || s.id === 4 : s.id <= estadoActual && s.id !== 4;
                  const isDisabled = isCancel ? !(s.id === 0 || s.id === 4) : s.id === 4;

                  return (
                    <div
                      key={s.id}
                      className={[
                        "omx-tl__step",
                        isActive ? "is-active" : "",
                        isDone ? "is-done" : "",
                        s.id === 4 ? "is-cancel" : "",
                        isDisabled ? "is-disabled" : "",
                      ].join(" ")}
                    >
                      <div className="omx-tl__rail">
                        <div className="omx-tl__dot" aria-hidden="true">
                          <StatusIcon estadoId={s.id} />
                        </div>
                        {idx !== stepInfo.length - 1 && <div className="omx-tl__line" />}
                      </div>

                      <div className="omx-tl__info">
                        <div className="omx-tl__topRow">
                          <div className="omx-tl__label">{s.nombre}</div>
                          <div className="omx-tl__date">{formatDate(s.fecha)}</div>
                        </div>

                        <div className="omx-tl__meta">
                          <span className="omx-tl__by">by</span>
                          <span className="omx-tl__user">{s.usuario ? fullName(s.usuario) : "—"}</span>
                          {s.usuario?.usuario ? (
                            <span className="omx-tl__username">@{s.usuario.usuario}</span>
                          ) : null}
                        </div>

                        {s.comentario ? <div className="omx-tl__comment">{s.comentario}</div> : null}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            <div className="omx-tl__right">
              <div className="omx-tl__card">
                <div className="omx-tl__cardTitle">Summary</div>

                <div className="omx-tl__kv">
                  <div className="omx-tl__k">Customer</div>
                  <div className="omx-tl__v">{pedido?.cliente_id ?? "—"}</div>
                </div>
                <div className="omx-tl__kv">
                  <div className="omx-tl__k">Total</div>
                  <div className="omx-tl__v">{money(pedido?.total_factura)}</div>
                </div>
                <div className="omx-tl__kv">
                  <div className="omx-tl__k">Created</div>
                  <div className="omx-tl__v">{formatDate(pedido?.fecha_creado)}</div>
                </div>

                <div className="omx-tl__divider" />

                <div className="omx-tl__cardTitle">Latest Change</div>
                {lastChange ? (
                  <div className="omx-tl__lastChange">
                    <div className="omx-tl__badge">
                      {lastChange.estado_anterior} → {lastChange.estado_nuevo}
                    </div>
                    <div className="omx-tl__badgeText">
                      <div className="omx-tl__badgeDate">
                        {formatDate(lastChange.fecha_creado || lastChange.fecha)}
                      </div>
                      <div className="omx-tl__badgeUser">
                        {lastChange.nombre} {lastChange.apellido}
                        {lastChange.usuario ? <span className="omx-tl__at">@{lastChange.usuario}</span> : null}
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="omx-tl__empty">No changes recorded yet.</div>
                )}

                <div className="omx-tl__divider" />

                <div className="omx-tl__cardTitle">History</div>
                <div className="omx-tl__raw">
                  {historial.length === 0 ? (
                    <div className="omx-tl__empty">No changes recorded yet.</div>
                  ) : (
                    [...historial]
                      .sort((a, b) => toTime(b.fecha_creado || b.fecha) - toTime(a.fecha_creado || a.fecha))
                      .map((h) => (
                        <div key={h.pedido_estado_historial_id} className="omx-tl__rawRow">
                          <div className="omx-tl__badge">
                            {h.estado_anterior} → {h.estado_nuevo}
                          </div>
                          <div className="omx-tl__badgeText">
                            <div className="omx-tl__badgeDate">{formatDate(h.fecha_creado || h.fecha)}</div>
                            <div className="omx-tl__badgeUser">
                              {h.nombre} {h.apellido}
                              {h.usuario ? <span className="omx-tl__at">@{h.usuario}</span> : null}
                            </div>
                            {h.comentario ? <div className="omx-tl__rawComment">{h.comentario}</div> : null}
                          </div>
                        </div>
                      ))
                  )}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default OrderTimelineModal;

